import React, { useEffect } from 'react';
import { DNSSubdomainField } from '../DNSSubdomainField';
import { SignupFormData, FormErrors, BusinessNameValidationState } from '../../types';
import { getFullAppDomain } from '../../config/endpoints';

interface PlatformSetupStepProps {
  formData: SignupFormData;
  errors: FormErrors;
  businessNameValidation: BusinessNameValidationState;
  updateField: (field: keyof SignupFormData, value: string | boolean) => void;
  onNext: () => void;
  onPrevious: () => void;
  onStepComplete?: (stepName: string) => void;
}

export const PlatformSetupStep: React.FC<PlatformSetupStepProps> = ({
  formData,
  errors,
  businessNameValidation,
  updateField,
  onNext,
  onPrevious,
  onStepComplete,
}) => {
  // Debug: Log what values this component receives
  console.log('🔍 PlatformSetupStep rendered with:', {
    companyName: formData.companyName,
    desiredSubdomain: formData.desiredSubdomain,
    businessNameValidation: businessNameValidation
  });

  // Ensure business name validation is triggered when component mounts with a pre-filled subdomain
  useEffect(() => {
    if (formData.desiredSubdomain && !businessNameValidation.isValidating && businessNameValidation.isAvailable === null) {
      console.log('🔄 Triggering business name validation for pre-filled subdomain:', formData.desiredSubdomain);
      // Trigger validation by simulating a field change
      updateField('desiredSubdomain', formData.desiredSubdomain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.desiredSubdomain, businessNameValidation.isValidating, businessNameValidation.isAvailable]);

  const isStepValid = () => {
    return (
      formData.desiredSubdomain &&
      // 'check_failed' means we could not reach the registry, not that the name
      // is taken. Blocking Continue on it turns an outage into a lost signup.
      (businessNameValidation.isAvailable === true ||
        businessNameValidation.status === 'check_failed') &&
      !errors.desiredSubdomain
    );
  };

  const handleNext = () => {
    if (isStepValid()) {
      // Track step completion in HubSpot
      if (onStepComplete) {
        onStepComplete('platform_setup');
      }
      onNext();
    }
  };

  // Check if the subdomain appears to be auto-generated from company name
  const generateSubdomainFromCompanyName = (companyName: string): string => {
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 63);
  };

  const isAutoFilled = formData.companyName && 
    formData.desiredSubdomain === generateSubdomainFromCompanyName(formData.companyName);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          Choose your Smartr365 address
        </h1>
        <p className="text-gray-600">
          This is your team's custom login page. Make it easy to remember.
        </p>
        {isAutoFilled && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">✨ We've suggested this based on your company name:</span> {formData.companyName}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="desiredSubdomain" className="block text-sm font-medium text-gray-700 mb-2">
            Domain
          </label>
          <DNSSubdomainField
            id="desiredSubdomain"
            name="desiredSubdomain"
            label=""
            value={formData.desiredSubdomain}
            onChange={(value) => updateField('desiredSubdomain', value)}
            validation={businessNameValidation}
            error={errors.desiredSubdomain}
            required={false}
          />
        </div>

        {/* Validating message */}
        {businessNameValidation.isValidating && formData.desiredSubdomain && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-blue-600 mr-2"></div>
              <div>
                <p className="text-sm font-medium text-blue-800">Checking availability...</p>
                <p className="text-sm text-blue-600">{getFullAppDomain(formData.desiredSubdomain)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onPrevious}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Previous
        </button>
        
        <button
          type="button"
          onClick={handleNext}
          disabled={!isStepValid()}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
};
